import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './driverTheme.css';
import { toast } from 'sonner';
import { clientPortalService } from '../services/clientPortal.service';
import { buildTourSteps } from './tourSteps';

const TourContext = createContext(null);
export const useTour = () => useContext(TourContext);

// Coordinates the guided product tour: fetches version-aware onboarding state +
// the caller's role/permissions, filters steps, and drives driver.js across
// pages. driver.js renders into document.body so it survives client-route
// changes (the layout stays mounted), letting the tour walk page-to-page.
export const TourProvider = ({ children }) => {
  const navigate = useNavigate();
  const navRef = useRef(navigate);
  navRef.current = navigate;

  const [status, setStatus] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [ready, setReady] = useState(false);
  const driverRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [st, perm] = await Promise.all([
          clientPortalService.getOnboardingStatus(),
          clientPortalService.getMyPermissions().catch(() => null),
        ]);
        if (!mounted) return;
        setStatus(st);
        setPermissions(perm);
      } catch (e) {
        // non-fatal: tour just won't auto-show
      } finally {
        if (mounted) setReady(true);
      }
    })();
    return () => {
      mounted = false;
      if (driverRef.current) {
        try { driverRef.current.destroy(); } catch (e) { /* noop */ }
      }
    };
  }, []);

  const runTour = useCallback((mode, seenVersion, tourVersion) => {
    const steps = buildTourSteps({ mode, seenVersion, permissions });
    if (!steps.length) {
      toast.info("You're all caught up — nothing new to show right now.");
      return;
    }

    if (driverRef.current) {
      try { driverRef.current.destroy(); } catch (e) { /* noop */ }
    }

    const persist = (kind) => {
      const fn = kind === 'complete'
        ? clientPortalService.completeOnboarding
        : clientPortalService.dismissOnboarding;
      Promise.resolve(fn(tourVersion)).catch(() => {});
      setStatus((s) => (s ? { ...s, mode: 'none', onboarding_completed: true } : s));
    };

    const d = driver({
      showProgress: true,
      allowClose: true,
      overlayColor: '#0f172a',
      stagePadding: 6,
      stageRadius: 10,
      popoverClass: 'maddonuts-tour',
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      doneBtnText: 'Done',
      progressText: '{{current}} of {{total}}',
      steps: steps.map((s) => ({
        element: s.element,
        popover: {
          title: s.title,
          description: s.description,
          side: s.side || 'right',
          align: s.align || 'start',
        },
      })),
      onHighlightStarted: (el, step, opts) => {
        const idx = opts?.state?.activeIndex ?? 0;
        const route = steps[idx]?.route;
        if (route) navRef.current(route);
      },
      onDestroyStarted: () => {
        const idx = d.getActiveIndex?.() ?? 0;
        const isLast = idx >= steps.length - 1;
        if (isLast) {
          persist('complete');
          toast.success('Tour complete! Replay it anytime from Help & Tour.');
        } else {
          persist('dismiss');
        }
        d.destroy();
      },
      onDestroyed: () => {
        driverRef.current = null;
      },
    });

    driverRef.current = d;

    const firstRoute = steps[0]?.route;
    if (firstRoute) navRef.current(firstRoute);
    // brief delay so the first page mounts before highlighting
    setTimeout(() => {
      try { d.drive(); } catch (e) { /* noop */ }
    }, 200);
  }, [permissions]);

  const startTour = useCallback(() => {
    const mode = status?.mode === 'whats_new' ? 'whats_new' : 'full';
    runTour(mode, status?.seen_version ?? 0, status?.tour_version ?? 1);
  }, [status, runTour]);

  const replayTour = useCallback(() => {
    Promise.resolve(clientPortalService.replayOnboarding()).catch(() => {});
    runTour('full', 0, status?.tour_version ?? 1);
  }, [status, runTour]);

  const dismissTour = useCallback(() => {
    Promise.resolve(clientPortalService.dismissOnboarding(status?.tour_version)).catch(() => {});
    setStatus((s) => (s ? { ...s, mode: 'none' } : s));
  }, [status]);

  const value = {
    ready,
    status,
    permissions,
    startTour,
    replayTour,
    dismissTour,
    showWelcome: ready && !!status && (status.mode === 'full' || status.mode === 'whats_new'),
    isWhatsNew: status?.mode === 'whats_new',
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};
